import { forwardRef } from 'react';
import type { MetricValue } from '@aurora-pvi/contracts';
import { cn, focusRing } from '../utils/cn';
import { Icon, type IconName, resolveIconToken } from '../primitives/Icon';
import { IconButton } from '../primitives/IconButton';
import { Skeleton } from '../primitives/Skeleton';
import { Tooltip } from '../primitives/Tooltip';
import { Sparkline } from './Sparkline';
import { ConfidenceBadge, SourceBadge } from './StatusBadges';
import {
  computeDelta,
  deltaSentiment,
  formatDelta,
  formatMetricValue,
  formatRelativeTime,
} from '../utils/format';

export interface KpiTileProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  metric: MetricValue;
  /** Leading glyph, matching the coloured KPI medallions in the reference. */
  iconToken?: string | null;
  iconTone?: 'action' | 'success' | 'attention' | 'teal' | 'danger' | 'navy';
  trend?: Array<number | null>;
  loading?: boolean;
  density?: 'comfortable' | 'compact';
  /** Opens the governed metric definition. Every tile must offer this. */
  onOpenDefinition?: (metricId: string) => void;
  /** Drill-through. When absent the tile is static, not a disabled button. */
  onClick?: (metric: MetricValue) => void;
  showSourceBadge?: boolean;
  showConfidence?: boolean;
}

const iconToneClasses = {
  action: 'bg-action text-action-contrast',
  success: 'bg-success text-success-contrast',
  attention: 'bg-attention text-attention-contrast',
  teal: 'bg-teal text-white',
  danger: 'bg-danger text-danger-contrast',
  navy: 'bg-navy text-navy-contrast',
} as const;

const sentimentClasses = {
  positive: 'text-success',
  negative: 'text-danger',
  neutral: 'text-content-secondary',
} as const;

const directionIcon: Record<'up' | 'down' | 'flat', IconName> = {
  up: 'arrow-up',
  down: 'arrow-down',
  flat: 'trend-flat',
};

export const KpiTile = forwardRef<HTMLDivElement, KpiTileProps>(function KpiTile(
  {
    metric,
    iconToken,
    iconTone = 'action',
    trend,
    loading = false,
    density = 'comfortable',
    onOpenDefinition,
    onClick,
    showSourceBadge = true,
    showConfidence = false,
    className,
    ...rest
  },
  ref,
) {
  const compact = density === 'compact';

  if (loading) {
    // The skeleton reproduces the final footprint exactly so that the tile does
    // not resize when the value arrives.
    return (
      <div
        ref={ref}
        aria-busy="true"
        aria-label={`${metric.label} — loading`}
        className={cn(
          'flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-surface shadow-card',
          compact ? 'p-3' : 'p-4',
          className,
        )}
        {...rest}
      >
        <div className="flex items-center gap-2">
          <Skeleton shape="circle" width={compact ? 24 : 32} height={compact ? 24 : 32} />
          <Skeleton shape="text" width="55%" />
        </div>
        <Skeleton height={compact ? 22 : 28} width="45%" />
        <Skeleton shape="text" width="70%" />
      </div>
    );
  }

  const unavailable = metric.status !== 'Available' && metric.status !== 'Partial';
  const formatted = formatMetricValue(metric.value, metric.unit);
  const delta = computeDelta(metric.value, metric.comparisonValue);
  const sentiment = delta ? deltaSentiment(delta.direction, metric.desiredDirection) : 'neutral';
  const interactive = Boolean(onClick);

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {iconToken ? (
            <span
              aria-hidden="true"
              className={cn(
                'flex shrink-0 items-center justify-center rounded-lg',
                compact ? 'h-7 w-7' : 'h-9 w-9',
                iconToneClasses[iconTone],
              )}
            >
              <Icon name={resolveIconToken(iconToken, 'activity')} size={compact ? 14 : 18} />
            </span>
          ) : null}
          <div className="flex min-w-0 flex-col">
            <span
              className={cn(
                'truncate font-medium text-content-secondary',
                compact ? 'text-dense' : 'text-dense-lg',
              )}
            >
              {metric.label}
            </span>
            <span className="truncate text-micro text-content-muted">{metric.metricId}</span>
          </div>
        </div>
        {onOpenDefinition ? (
          <IconButton
            icon="info"
            size="sm"
            label={`How ${metric.label} is calculated`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenDefinition(metric.metricId);
            }}
          />
        ) : null}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          {unavailable || formatted === null ? (
            // A failed or stale source renders as words, never as a zero.
            <span
              className={cn(
                'flex items-center gap-1.5 font-semibold text-content-muted',
                compact ? 'text-body' : 'text-body-lg',
              )}
            >
              <Icon
                name={metric.status === 'Error' ? 'alert' : 'x-circle'}
                size={compact ? 14 : 16}
                className={metric.status === 'Error' ? 'text-danger' : undefined}
              />
              {metric.status === 'Stale' ? 'Last known value unavailable' : 'Not available'}
            </span>
          ) : (
            <span
              className={cn(
                'truncate font-semibold tabular-nums text-content',
                compact ? 'text-section' : 'text-metric',
              )}
            >
              {formatted}
            </span>
          )}

          {delta && !unavailable ? (
            <span className={cn('flex items-center gap-1 text-dense', sentimentClasses[sentiment])}>
              <Icon name={directionIcon[delta.direction]} size={12} strokeWidth={2.25} />
              <span className="font-semibold tabular-nums">{formatDelta(delta, metric.unit)}</span>
              <span className="text-content-muted">
                {metric.comparisonLabel ?? 'vs comparison period'}
              </span>
            </span>
          ) : metric.unavailableReason ? (
            <span className="line-clamp-2 text-dense text-content-secondary">
              {metric.unavailableReason}
            </span>
          ) : null}
        </div>

        {trend && trend.length > 1 && !unavailable ? (
          <Sparkline
            points={trend}
            width={compact ? 64 : 96}
            height={compact ? 20 : 28}
            filled
            stroke={
              sentiment === 'negative'
                ? 'var(--color-chart-6)'
                : sentiment === 'positive'
                  ? 'var(--color-chart-3)'
                  : 'var(--color-chart-1)'
            }
            accessibleSummary={`Trend for ${metric.label} over the selected period.`}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {showSourceBadge ? (
          <SourceBadge
            sourceMode={metric.sourceMode}
            status={metric.status}
            refreshedAt={metric.refreshedAt}
            showSystem={false}
          />
        ) : null}
        {showConfidence && metric.confidence ? <ConfidenceBadge band={metric.confidence} /> : null}
        <Tooltip content={`Definition ${metric.definitionVersion}`}>
          <span
            tabIndex={0}
            className={cn(
              'flex items-center gap-1 rounded-sm text-micro text-content-muted',
              focusRing,
            )}
          >
            <Icon name="clock" size={11} />
            {formatRelativeTime(metric.refreshedAt)}
          </span>
        </Tooltip>
      </div>
    </>
  );

  return (
    <div
      ref={ref}
      className={cn(
        'group relative flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-surface text-left shadow-card',
        'transition-colors duration-fast ease-standard',
        'focus-within:border-action',
        compact ? 'p-3' : 'p-4',
        unavailable && 'border-line-strong bg-surface-sunken',
        interactive && 'cursor-pointer hover:border-action hover:bg-surface-hover',
        className,
      )}
      {...rest}
    >
      {/*
        Drill-through is an overlay button rather than a button wrapping the
        tile, because the definition control is itself a button and buttons
        cannot nest. Interactive children sit above the overlay via z-index.
      */}
      {interactive ? (
        <button
          type="button"
          onClick={() => onClick?.(metric)}
          className={cn('absolute inset-0 z-0 rounded-xl', focusRing)}
        >
          <span className="sr-only">View detail for {metric.label}</span>
        </button>
      ) : null}
      <div
        className={cn(
          'relative z-10 flex flex-col gap-2',
          interactive &&
            'pointer-events-none [&_[tabindex]]:pointer-events-auto [&_a]:pointer-events-auto [&_button]:pointer-events-auto',
        )}
      >
        {body}
      </div>
    </div>
  );
});
